// Create new file: src/components/PaymentSuccess.jsx

import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  Dashboard,
  ArrowForward
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import stripeService from '../services/stripeService';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    // Handle payment success
    if (sessionId) {
      stripeService.handlePaymentSuccess(sessionId);
    }
    
    // Auto-redirect after 5 seconds
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [sessionId, navigate]);

  return (
    <Container maxWidth="md" sx={{ py: 8, minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
      <Paper sx={{ p: 6, textAlign: 'center', width: '100%' }}>
        {/* Success Icon */}
        <Box sx={{ mb: 3 }}>
          <CheckCircle 
            sx={{ 
              fontSize: 80, 
              color: 'success.main',
              filter: 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))'
            }} 
          />
        </Box>
        
        {/* Success Message */}
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
          Payment Successful!
        </Typography>
        
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
          Thank you for upgrading to XAUPilot Premium! Your subscription is now active and you have access to all premium features.
        </Typography>
        
        {/* Features Alert */}
        <Alert severity="success" sx={{ mb: 4, textAlign: 'left' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            🎉 You now have access to:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>Real-time trading signals</li>
            <li>Instant entry and exit levels</li>
            <li>Advanced performance metrics</li>
            <li>Premium market analysis</li>
          </ul>
        </Alert>
        
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Dashboard />}
            onClick={() => navigate('/dashboard')}
            sx={{ px: 4, py: 1.5 }}
          >
            Go to Dashboard
          </Button>
          
          <Button
            variant="outlined"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/subscription')}
            sx={{ px: 4, py: 1.5 }}
          >
            Manage Subscription
          </Button>
        </Box>
        
        {/* Auto-redirect notice */}
        <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Redirecting to dashboard in 5 seconds...
          </Typography>
        </Box>
        
        {/* Session ID for debugging */}
        {sessionId && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
            Session ID: {sessionId}
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default PaymentSuccess;